import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { UnifiedChartData } from "./types";
import { formatNumber } from "../../../api/DataTypeUtils";
import { colors } from "../../../board/components/TaskDetail/ColorPicker";

type Props = {
  data: UnifiedChartData;
  height?: number | string;
  goalValue: number;
  angle?: number;
  barSize?: number;
  categoryGap?: number | string;
  barColors?: string[];
};

const GoalBarChart = ({
  data,
  height = 440,
  goalValue = 0,
  angle,
  barSize = 12,
  categoryGap = "55%",
  barColors = colors,
}: Props) => {
  const rows = data.labels.map((label, i) => {
    const primary = data.series[1] ? data.series[1] : data.series[0];
    const actual = primary ? (primary.data[i] ?? 0) : 0;
    const goal = goalValue;
    const remaining = Math.max(goal - actual, 0);
    return {
      name: label,
      actual,
      goal,
      remaining,
    };
  });

  const maxVal = Math.max(0, goalValue);
  const yMax = Math.ceil(maxVal * 1 || 10);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const values = payload.reduce((acc, p) => {
        acc[p.dataKey] = p.value;
        return acc;
      }, {});
      return (
        <div className="recharts-default-tooltip px-3 py-2 rounded-md bg-[#111827]">
          <p className="text-[#D1D5DB]">{label}</p>
          <p className="text-[#ffffff]">
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ backgroundColor: barColors[0] }}
            ></span>
            <span className="ml-2">{`${formatNumber(values["actual"] || 0)}/${formatNumber(
              values["goal"] || 0
            )}`}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  const GoalDot = (props) => {
    const { cx, cy } = props;
    return (
      <svg
        x={cx - 16.5}
        y={cy - 16.5}
        width="32"
        height="18"
        viewBox="0 0 32 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_221126_32942)">
          <path
            d="M16 11C18.7614 11 21 8.76142 21 6C21 3.23858 18.7614 1 16 1C13.2386 1 11 3.23858 11 6C11 8.76142 13.2386 11 16 11Z"
            stroke="#1F2937"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16 9C17.6569 9 19 7.65685 19 6C19 4.34315 17.6569 3 16 3C14.3431 3 13 4.34315 13 6C13 7.65685 14.3431 9 16 9Z"
            stroke="#1F2937"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16 7C16.5523 7 17 6.55228 17 6C17 5.44772 16.5523 5 16 5C15.4477 5 15 5.44772 15 6C15 6.55228 15.4477 7 16 7Z"
            stroke="#1F2937"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
        <rect y="16" width="1" height="2" fill="#1F2937" />
        <rect x="1" y="16" width="30" height="1" fill="#1F2937" />
        <rect x="31" y="16" width="1" height="2" fill="#1F2937" />
        <defs>
          <clipPath id="clip0_221126_32942">
            <rect width="12" height="12" fill="white" transform="translate(10)" />
          </clipPath>
        </defs>
      </svg>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart
        data={rows}
        margin={{ top: 20, right: 10, bottom: 0, left: 0 }}
        barCategoryGap={categoryGap}
      >
        <CartesianGrid stroke="#F3F4F6" />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 12, fill: "#6B7280", fontWeight: 500 }}
          tickLine={false}
          axisLine={{ stroke: "#E5E7EB" }}
          angle={angle}
        />
        <YAxis
          domain={[0, yMax]}
          tick={{ fontSize: 12, fill: "#6B7280", fontWeight: 500 }}
          tickLine={false}
          axisLine={{ stroke: "#E5E7EB" }}
          tickFormatter={(value) => formatNumber(value)}
        />
        <Tooltip content={CustomTooltip} />
        <Bar dataKey="actual" name="Actual" stackId="a" barSize={barSize} fill={barColors[0]} />
        <Bar dataKey="remaining" name="Remaining" stackId="a" barSize={barSize} fill="#CBD5E1" />
        <Line
          type="linear"
          dataKey="goal"
          name="Goal Target"
          stroke="transparent"
          dot={<GoalDot />}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default GoalBarChart;
